import { useState, useCallback, useEffect, useRef } from 'react';
import {
  useKeyPress,
  useReactFlow,
  getConnectedEdges,
  useStore
} from '@xyflow/react';

const DEFAULT_DUPLICATE_OFFSET = { x: 40, y: 40 };

const getConnectedInternalEdges = (nodes, edges) => {
  const selectedNodeIds = new Set(nodes.map(node => node.id));

  return getConnectedEdges(nodes, edges).filter(
    edge => selectedNodeIds.has(edge.source) && selectedNodeIds.has(edge.target)
  );
};

const getDescendantNodeIds = (startNodeIds, nodes, edges) => {
  const visited = new Set(startNodeIds);
  const queue = [...startNodeIds];
  const nodeMap = new Map(nodes.map(node => [node.id, node]));

  while (queue.length) {
    const currentNodeId = queue.shift();

    edges.forEach(edge => {
      if (edge.source !== currentNodeId || visited.has(edge.target)) {
        return;
      }

      const targetNode = nodeMap.get(edge.target);

      if (
        !targetNode ||
        targetNode.type === 'begin' ||
        targetNode.type === 'end'
      ) {
        return;
      }

      visited.add(targetNode.id);
      queue.push(targetNode.id);
    });
  }

  return Array.from(visited);
};

const replaceMappedIds = (value, idMap) => {
  if (typeof value !== 'string') {
    return value ?? null;
  }

  let nextValue = value;

  idMap.forEach((newId, oldId) => {
    nextValue = nextValue.replaceAll(oldId, newId);
  });

  return nextValue;
};

const remapWorkflowNodeData = (data, oldId, newId) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const nextData = { ...data };
  delete nextData.onChange;

  if (typeof nextData.id === 'string' && nextData.id === oldId) {
    nextData.id = newId;
  }

  if (Array.isArray(nextData.transitions)) {
    nextData.transitions = nextData.transitions.map(transition => ({
      ...transition,
      id: replaceMappedIds(transition.id, new Map([[oldId, newId]])),
      sourceHandle: replaceMappedIds(
        transition.sourceHandle,
        new Map([[oldId, newId]])
      )
    }));
  }

  if (Array.isArray(nextData.userOptions)) {
    nextData.userOptions = nextData.userOptions.map(userOption => ({
      ...userOption,
      id: replaceMappedIds(userOption.id, new Map([[oldId, newId]]))
    }));
  }

  if (Array.isArray(nextData.variables)) {
    nextData.variables = nextData.variables.map(variable => ({
      ...variable,
      id: replaceMappedIds(variable.id, new Map([[oldId, newId]]))
    }));
  }

  if (typeof nextData.successTransitionId === 'string') {
    nextData.successTransitionId = replaceMappedIds(
      nextData.successTransitionId,
      new Map([[oldId, newId]])
    );
  }

  if (typeof nextData.failureTransitionId === 'string') {
    nextData.failureTransitionId = replaceMappedIds(
      nextData.failureTransitionId,
      new Map([[oldId, newId]])
    );
  }

  return nextData;
};

const createNodeIdGenerator = nodes => {
  let maxNodeNumber = nodes.reduce((max, node) => {
    const match =
      typeof node.id === 'string' ? node.id.match(/^node-(\d+)$/) : null;

    if (!match) {
      return max;
    }

    const value = parseInt(match[1], 10);
    return Number.isNaN(value) ? max : Math.max(max, value);
  }, 2);

  return () => {
    maxNodeNumber += 1;

    if (maxNodeNumber === 2) {
      maxNodeNumber += 1;
    }

    return `node-${maxNodeNumber}`;
  };
};

export function useCopyPaste(options = {}) {
  const { bindShortcuts = true, manageDomEvents = true } = options;
  const mousePosRef = useRef({ x: 0, y: 0 });
  const rfDomNode = useStore(state => state.domNode);

  const { getNodes, setNodes, getEdges, setEdges, screenToFlowPosition } =
    useReactFlow();

  // Set up the paste buffers to store the copied nodes and edges.
  const [bufferedNodes, setBufferedNodes] = useState([]);
  const [bufferedEdges, setBufferedEdges] = useState([]);

  // initialize the copy/paste hook
  // 1. remove native copy/paste/cut handlers
  // 2. add mouse move handler to keep track of the current mouse position
  useEffect(() => {
    if (!manageDomEvents || !rfDomNode) {
      return undefined;
    }

    const events = ['cut', 'copy', 'paste'];

    const preventDefault = e => e.preventDefault();

    const onMouseMove = event => {
      mousePosRef.current = {
        x: event.clientX,
        y: event.clientY
      };
    };

    for (const event of events) {
      rfDomNode.addEventListener(event, preventDefault);
    }

    rfDomNode.addEventListener('mousemove', onMouseMove);

    return () => {
      for (const event of events) {
        rfDomNode.removeEventListener(event, preventDefault);
      }

      rfDomNode.removeEventListener('mousemove', onMouseMove);
    };
  }, [manageDomEvents, rfDomNode]);

  const getCopyPayload = useCallback(
    (nodeIds, options = {}) => {
      const { includeChildren = false } = options;
      const allNodes = getNodes();
      const allEdges = getEdges();
      const baseNodeIds = Array.isArray(nodeIds)
        ? nodeIds
        : allNodes.filter(node => node.selected).map(node => node.id);
      const resolvedNodeIds = includeChildren
        ? getDescendantNodeIds(baseNodeIds, allNodes, allEdges)
        : baseNodeIds;
      const selectedNodes = allNodes.filter(node =>
        resolvedNodeIds.includes(node.id)
      );
      const selectedEdges = getConnectedInternalEdges(selectedNodes, allEdges);

      return { selectedNodes, selectedEdges };
    },
    [getNodes, getEdges]
  );

  const createPastePayload = useCallback(
    (nodesToPaste, edgesToPaste, options = {}) => {
      if (!nodesToPaste.length) {
        return { newNodes: [], newEdges: [] };
      }

      const {
        position,
        offset = DEFAULT_DUPLICATE_OFFSET,
        selectPasted = true
      } = options;

      const minX = Math.min(...nodesToPaste.map(node => node.position?.x ?? 0));
      const minY = Math.min(...nodesToPaste.map(node => node.position?.y ?? 0));
      const basePosition = position || {
        x: minX + offset.x,
        y: minY + offset.y
      };
      const nextNodeId = createNodeIdGenerator(getNodes());
      const nodeIdMap = new Map();
      const now = Date.now();

      nodesToPaste.forEach(node => {
        nodeIdMap.set(node.id, nextNodeId());
      });

      const newNodes = nodesToPaste.map(node => {
        const newId = nodeIdMap.get(node.id);
        const nextNode = {
          ...node,
          id: newId,
          position: {
            x: basePosition.x + ((node.position?.x ?? 0) - minX),
            y: basePosition.y + ((node.position?.y ?? 0) - minY)
          },
          data: remapWorkflowNodeData(node.data, node.id, newId),
          selected: selectPasted,
          dragging: false
        };

        delete nextNode.measured;
        delete nextNode.positionAbsolute;

        return nextNode;
      });

      const newEdges = edgesToPaste.map((edge, index) => ({
        ...edge,
        id: `${edge.id}-${now}-${index}`,
        source: nodeIdMap.get(edge.source) || edge.source,
        target: nodeIdMap.get(edge.target) || edge.target,
        sourceHandle: replaceMappedIds(edge.sourceHandle, nodeIdMap),
        targetHandle: replaceMappedIds(edge.targetHandle, nodeIdMap),
        selected: selectPasted
      }));

      return { newNodes, newEdges };
    },
    [getNodes]
  );

  const copy = useCallback(() => {
    const { selectedNodes, selectedEdges } = getCopyPayload();

    setBufferedNodes(selectedNodes);
    setBufferedEdges(selectedEdges);
  }, [getCopyPayload]);

  const cut = useCallback(() => {
    const { selectedNodes, selectedEdges } = getCopyPayload();

    setBufferedNodes(selectedNodes);
    setBufferedEdges(selectedEdges);

    // A cut action needs to remove the copied nodes and edges from the graph.
    setNodes(nodes => nodes.filter(node => !node.selected));
    setEdges(edges => edges.filter(edge => !selectedEdges.includes(edge)));
  }, [getCopyPayload, setNodes, setEdges]);

  const paste = useCallback(
    (
      { x: pasteX, y: pasteY } = screenToFlowPosition({
        x: mousePosRef.current.x,
        y: mousePosRef.current.y
      })
    ) => {
      if (!bufferedNodes.length) {
        return;
      }

      const { newNodes, newEdges } = createPastePayload(
        bufferedNodes,
        bufferedEdges,
        {
          position: { x: pasteX, y: pasteY }
        }
      );

      setNodes(nodes => [
        ...nodes.map(node => ({ ...node, selected: false })),
        ...newNodes
      ]);
      setEdges(edges => [
        ...edges.map(edge => ({ ...edge, selected: false })),
        ...newEdges
      ]);
    },
    [
      bufferedNodes,
      bufferedEdges,
      createPastePayload,
      screenToFlowPosition,
      setNodes,
      setEdges
    ]
  );

  const duplicateNodes = useCallback(
    (nodeIds, options = {}) => {
      const { selectedNodes, selectedEdges } = getCopyPayload(nodeIds);

      if (!selectedNodes.length) {
        return [];
      }

      setBufferedNodes(selectedNodes);
      setBufferedEdges(selectedEdges);

      const { newNodes, newEdges } = createPastePayload(
        selectedNodes,
        selectedEdges,
        options
      );

      setNodes(nodes => [
        ...nodes.map(node => ({ ...node, selected: false })),
        ...newNodes
      ]);
      setEdges(edges => [
        ...edges.map(edge => ({ ...edge, selected: false })),
        ...newEdges
      ]);

      return newNodes;
    },
    [getCopyPayload, createPastePayload, setNodes, setEdges]
  );

  const duplicateNodesWithChildren = useCallback(
    (nodeIds, options = {}) => {
      const { selectedNodes, selectedEdges } = getCopyPayload(nodeIds, {
        includeChildren: true
      });

      if (!selectedNodes.length) {
        return [];
      }

      setBufferedNodes(selectedNodes);
      setBufferedEdges(selectedEdges);

      const { newNodes, newEdges } = createPastePayload(
        selectedNodes,
        selectedEdges,
        options
      );

      setNodes(nodes => [
        ...nodes.map(node => ({ ...node, selected: false })),
        ...newNodes
      ]);
      setEdges(edges => [
        ...edges.map(edge => ({ ...edge, selected: false })),
        ...newEdges
      ]);

      return newNodes;
    },
    [getCopyPayload, createPastePayload, setNodes, setEdges]
  );

  useShortcut(['Meta+x', 'Control+x'], cut, bindShortcuts);
  useShortcut(['Meta+c', 'Control+c'], copy, bindShortcuts);
  useShortcut(['Meta+v', 'Control+v'], paste, bindShortcuts);

  return {
    cut,
    copy,
    paste,
    duplicateNodes,
    duplicateNodesWithChildren,
    bufferedNodes,
    bufferedEdges
  };
}

function useShortcut(keyCode, callback, enabled = true) {
  const [didRun, setDidRun] = useState(false);
  const shouldRun = useKeyPress(keyCode);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (shouldRun && !didRun) {
      callback();
      setDidRun(true);
    } else {
      setDidRun(shouldRun);
    }
  }, [shouldRun, didRun, callback, enabled]);
}

export default useCopyPaste;
